import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { X, CreditCard, Info, Check, AlertTriangle, Loader2 } from "lucide-react";

export default function DepositModal({ open, onClose, onSuccess }) {
  const [amount, setAmount] = useState("25");
  const [limits, setLimits] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(""); setDone(null);
    api.get("/banking/deposit/limits")
      .then(r => setLimits(r.data))
      .catch(e => setError(formatErr(e.response?.data?.detail) || e.message));
  }, [open]);

  // Handle return-from-Stripe (success url has ?stripe_session_id=...)
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancelled");
    if (cancelled) {
      setError("Payment was cancelled.");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    pollStatus(sid, 0);
    // Clean URL immediately to avoid double-polling on refresh
    window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pollStatus = async (sessionId, attempt) => {
    if (attempt > 8) {
      setError("Payment is still processing. Check Transactions in a minute.");
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/banking/deposit/stripe/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setDone({
          amount: data.amount,
          settled: data.settled,
          note: data.settled
            ? "Your Spending balance is being credited now."
            : "Payment received. Spending credit is pending — usually settles within minutes.",
        });
        if (onSuccess) onSuccess(data);
        setBusy(false);
      } else if (data.status === "expired") {
        setError("Stripe session expired. Please try again.");
        setBusy(false);
      } else {
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      }
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  if (!open) return null;

  const amt = parseFloat(amount || "0");
  const validAmt = limits
    && amt >= limits.min_per_txn
    && amt <= limits.max_per_txn
    && amt <= limits.remaining_this_month;

  const continueToPayment = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        "/banking/deposit/stripe/checkout",
        { amount: amt },
        { headers: { "Origin": window.location.origin } },
      );
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="deposit-modal">
      <div className="bg-cream rounded-2xl border border-sand p-7 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
              Spending account
            </div>
            <h3 className="font-serif text-2xl mt-1">Deposit from debit card</h3>
          </div>
          <button onClick={onClose} className="text-sage hover:text-ink"
                  data-testid="deposit-close-btn" disabled={busy}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="mt-6 space-y-4" data-testid="deposit-success">
            <div className="bg-mint border border-forest/30 rounded-xl p-5 flex items-start gap-3">
              <Check className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
              <div className="text-sm text-ink">
                <div className="font-medium">${done.amount?.toFixed(2)} received</div>
                <p className="text-xs text-moss mt-2 leading-relaxed">{done.note}</p>
              </div>
            </div>
            <button onClick={onClose}
                    className="w-full bg-forest text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-forest-deep"
                    data-testid="deposit-done-btn">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 bg-cream-warm border border-sand-warm rounded-lg p-3 flex items-start gap-2"
                 data-testid="deposit-debit-only-notice">
              <Info className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" />
              <p className="text-xs text-moss leading-relaxed">
                Use a debit card for instant settlement. Powered by Stripe — your card details are
                entered on Stripe&apos;s secure page; Withdraw Guard never sees them.
              </p>
            </div>

            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
                Amount (USD)
              </label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-2xl font-serif text-ink">$</span>
                <input
                  type="number" min={limits?.min_per_txn || 10}
                  max={limits?.max_per_txn || 500} step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className="w-full bg-white border border-sand rounded-lg px-4 py-3 text-2xl font-serif"
                  data-testid="deposit-amount-input"
                  disabled={busy}
                />
              </div>
              {limits && (
                <div className="mt-2 text-[11px] text-sage font-mono"
                     data-testid="deposit-limits-line">
                  ${limits.min_per_txn.toFixed(0)}–${limits.max_per_txn.toFixed(0)} per deposit ·
                  ${limits.remaining_this_month.toFixed(2)} remaining this month
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg flex items-start gap-2"
                   data-testid="deposit-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={continueToPayment}
              disabled={!validAmt || busy}
              className="mt-6 w-full bg-ink text-cream rounded-lg px-5 py-3 text-sm font-medium hover:bg-ink-deep disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="deposit-continue-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {busy ? "Redirecting…" : `Continue to payment · $${(amt || 0).toFixed(2)}`}
            </button>
            <p className="mt-3 text-[10px] text-sage leading-relaxed text-center">
              You&apos;ll be taken to Stripe&apos;s secure checkout page to enter your card details.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
